import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import { Store } from '@ngrx/store';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import * as AppointmentActions from './appointment.actions';
import { AppState } from '../app.states';
import * as AppSelectors from '../app/app.selectors';
import { APIService } from '../../services/api/api.service';
import { format } from 'date-fns';
import { of } from 'rxjs';
import { loadCustomer } from '../customer/customer.actions';

@Injectable()
export class AppointmentEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private apiService: APIService,
    private notification: NzNotificationService
  ) // private clientService: ClientService
  {}

  loadAppointments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.loadAppointments),
      switchMap(action =>
        this.apiService.getAppointments({ ...action.filters }).pipe(
          map(appointments =>
            AppointmentActions.loadAppointmentsSuccess({ appointments })
          ),
          catchError(error =>
            of(AppointmentActions.loadAppointmentsFailure({ error }))
          )
        )
      )
    )
  );

  /* ----------------------------- Get Appointments By Id ----------------------------- */

  getAppointmentById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.getAppointmentById),
      switchMap(action =>
        this.apiService.getAppointmentById(action.id).pipe(
          map(appointment =>
            AppointmentActions.getAppointmentByIdSuccess({ appointment })
          ),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              `There was an error getting Appointment ${action.id}`
            );
            return of(AppointmentActions.getAppointmentByIdFailure({ error }));
          })
        )
      )
    )
  );

  fetchMySeenTodayAppointments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.fetchMySeenTodayAppointments),
      withLatestFrom(this.store.select(AppSelectors.getSelectedLayout)),
      switchMap(([action, selectedLayout]) =>
        this.apiService
          .getMySeenAppointments(
            format(new Date(), 'yyyy-MM-dd'),
            selectedLayout.organization
          )
          .pipe(
            map(appointments =>
              AppointmentActions.fetchMySeenTodayAppointmentsSuccess({
                appointments
              })
            ),
            catchError(error =>
              of(
                AppointmentActions.fetchMySeenTodayAppointmentsFailure({
                  error
                })
              )
            )
          )
      )
    )
  );

  createAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.createAppointment),
      switchMap(action =>
        this.apiService.createAppointment(action.appointment).pipe(
          tap(() => this.notification.success('Successfully Created.', '')),
          map(appointment =>
            AppointmentActions.createAppointmentSuccess({ appointment })
          ),
          catchError(error =>
            of(AppointmentActions.createAppointmentFailure({ error }))
          )
        )
      )
    )
  );

  updateAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.updateAppointment),
      switchMap(action =>
        this.apiService.updateAppointment(action.id, action.appointment).pipe(
          tap(() => this.notification.success('Successfully updated.', '')),
          map(appointment =>
            AppointmentActions.updateAppointmentSuccess({ appointment })
          ),
          catchError(error =>
            of(AppointmentActions.updateAppointmentFailure({ error }))
          )
        )
      )
    )
  );

  pullAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentActions.pullAppointment),
      switchMap(() =>
        this.apiService.pullAppointment().pipe(
          tap(appointment => {
            if (!appointment) {
              this.notification.success(
                'No appointments found',
                'Try again later.'
              );
            }
          }),
          map(appointment =>
            AppointmentActions.pullAppointmentSuccess({ appointment })
          ),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error pulling appointment.'
            );
            return of(AppointmentActions.pullAppointmentFailure({ error }));
          })
        )
      )
    )
  );

  pullAppointmentSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppointmentActions.pullAppointmentSuccess),
        tap(({ appointment }) => {
          const custId: string =
            appointment.relatedToLead['_id'] || appointment.relatedToLead;
          this.store.dispatch(loadCustomer({ id: custId }));
        })
      ),
    { dispatch: false }
  );
}
